import { makeSeededRNG } from './rng.js';
import Game from './Game.js';

import mediumSeeds from './seeds/medium.json' with { type: 'json' };
import easySeeds from './seeds/easy.json' with { type: 'json' };
import hardSeeds from './seeds/hard.json' with { type: 'json' };
import extremeSeeds from './seeds/extreme.json' with { type: 'json' };

const SEED_MODULUS = 99999999999;
const TARGET = 10;

class GameGenerator {
  constructor() {
    this.nextSeed = Math.floor(Math.random() * SEED_MODULUS) % SEED_MODULUS;
  }

  getSize() {
    throw new Error("getSize() must be implemented by subclass");
  }

  hasSubrectangleSum(grid) {
    const rows = grid.length;
    const cols = grid[0].length;

    // Iterate over all pairs of row boundaries
    for (let top = 0; top < rows; top++) {
      const colSums = new Array(cols).fill(0);

      for (let bottom = top; bottom < rows; bottom++) {
        // Update column sums between rows top and bottom
        for (let col = 0; col < cols; col++) {
          colSums[col] += grid[bottom][col];
        }

        // Now check if any subarray in colSums sums to target
        const seen = new Set([0]);
        let acc = 0;
        for (const val of colSums) {
          acc += val;
          if (seen.has(acc - TARGET)) {
            return true;
          }
          seen.add(acc);
        }
      }
    }

    return false;
  }

  generateNumbers(x, y) {
    const rng = makeSeededRNG(this.nextSeed);
    const numbers_flat = [];

    for (let i = 0; i < (x * y) - 2; i++) {
      numbers_flat.push(Math.floor(((rng()) ** 1.5) * 9) + 1);
    }

    const sum = numbers_flat.reduce((a, b) => a + b, 0);
    const needed = (10 - (sum % 10)) % 10; // How much more we need (0-9)

    // Find two numbers (1-9) that sum to `needed` or `needed + 10`
    const target = needed === 0 ? 10 : needed; // If needed is 0, we need two nums summing to 10
    const a = Math.max(1, target - 9); // Smallest valid first number
    const b = target - a;

    // If b is out of range, use TARGET + 10
    if (b < 1 || b > 9) {
      const target2 = target + 10;
      const a2 = Math.max(1, target2 - 9);
      numbers_flat.push(a2, target2 - a2);
    } else {
      numbers_flat.push(a, b);
    }

    // Shuffle
    for (let i = numbers_flat.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [numbers_flat[i], numbers_flat[j]] = [numbers_flat[j], numbers_flat[i]];
    }

    // Convert to 2D
    const numbers = [];
    for (let i = 0; i < x; i++) {
      numbers.push(numbers_flat.slice(i * y, (i + 1) * y));
    }

    return numbers;
  }

  isValidBoard(numbers) {
    if (!this.hasSubrectangleSum(numbers)) {
      return false;
    }
    const numbers_flat = numbers.flat()
    const sum = numbers_flat.reduce((a, b) => a + b, 0);
    if (sum % TARGET !== 0) {
      return false;
    }

    const max = numbers_flat.reduce((a, b) => a > b ? a : b);
    const min = numbers_flat.reduce((a, b) => a < b ? a : b);
    if (max >= TARGET || min < 1) {
      return false;
    }

    return true;
  }

  generate() {
    this.incrementSeed();
    const { x, y } = this.getSize();
    const numbers = this.generateNumbers(x, y);
    if (!this.isValidBoard(numbers)) {
      console.log("Regenerating game, board is invalid");
      return this.generate();
    }
    return new Game(numbers);
  }

  incrementSeed() {
    this.nextSeed = (this.nextSeed + 1) % SEED_MODULUS;
  }
}

class EasyGenerator extends GameGenerator {
  getSize() {
    return { x: 5, y: 5 };
  }
}

class MediumGenerator extends GameGenerator {
  getSize() {
    return { x: 7, y: 7 };
  }
}

class HardGenerator extends GameGenerator {
  getSize() {
    return { x: 10, y: 10 };
  }
}

class ExtremeGenerator extends GameGenerator {
  getSize() {
    return { x: 15, y: 10 };
  }
}

class CachedGenerator extends GameGenerator {
  constructor() {
      super();
      this.seedBank = this.getSeedBank();
      this.seedIndex = Math.floor(Math.random() * this.seedBank.length);
      this.nextSeed = this.seedBank[this.seedIndex];
  }

  incrementSeed() {
    // The step is 1..length-1, never 0. A zero step left seedIndex where it
    // was and handed back the board just played - and because generate()
    // recurses on an invalid board without changing anything else, a zero step
    // there would have recursed on the identical seed forever.
    const step = 1 + Math.floor(Math.random() * (this.seedBank.length - 1));
    this.seedIndex = (this.seedIndex + step) % this.seedBank.length;
    this.nextSeed = this.seedBank[this.seedIndex];
  }


  generate() {
    const game = super.generate();
    // Promise.resolve().then(() => {
    //   if (game.isClearable()) {
    //     console.log(`Game clearable with seed ${this.nextSeed}`);
    //   } else {
    //     console.error(`Game NOT clearable with seed ${this.nextSeed}`);
    //   }
    // });
    return game;
  }
}

class MediumCachedGenerator extends CachedGenerator {
  getSeedBank() {
    return mediumSeeds
  }

  getSize() {
    return { x: 7, y: 7 };
  }
}

class EasyCachedGenerator extends CachedGenerator {
  getSeedBank() {
    return easySeeds
  }

  getSize() {
    return { x: 5, y: 5 };
  }
}

class HardCachedGenerator extends CachedGenerator {
  getSeedBank() {
    return hardSeeds
  }

  getSize() {
    return { x: 10, y: 10 };
  }
}

class ExtremeCachedGenerator extends CachedGenerator {
  getSeedBank() {
    return extremeSeeds
  }

  getSize() {
    return { x: 15, y: 10 };
  }
}


export default GameGenerator;
export { EasyGenerator, MediumGenerator, HardGenerator, ExtremeGenerator, ExtremeCachedGenerator, MediumCachedGenerator, EasyCachedGenerator, HardCachedGenerator};
